"use client";

// "Apply" block of /hiring (#apply), rewritten in the v2 language on components/ds. It carries its own
// <section id="apply">, so the page drops it straight into <main>.
// Behaviour kept from the previous form, word for word: the same fields under the same FormData names, the same
// validation (email, resume required, .pdf/.doc/.docx, 5 MB) with the same messages, the same anti-bot pair
// (_gotcha honeypot, _elapsed since mount), the same POST of a multipart FormData to /api/apply, and the same
// success and failure copy. `initialRole` still arrives preselected from /hiring?role=<slug>#apply.
// Added, as on the homepage form: aria-invalid + aria-describedby on the fields in error, focus on the first one,
// focus on the success block, and a button that is aria-disabled while sending (disabled would drop the focus).

import { useEffect, useRef, useState } from "react";
import Button from "@/components/ds/button";
import Field from "@/components/ds/field";
import FileField from "@/components/ds/file-field";
import SectionHeading from "@/components/ds/section-heading";
import Surface from "@/components/ds/surface";
import Reveal from "@/components/motion/reveal";
import { stagger } from "@/components/motion/stagger";
import "./apply-form.css";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ALLOWED_EXT = /\.(pdf|doc|docx)$/i;

/** What the validation reads from the attached file; a File has both. */
export type ResumeFile = { name: string; size: number };

export type ApplyErrors = {
  name: boolean;
  email: boolean;
  /** null when the resume is fine, otherwise the message to show. */
  resume: string | null;
};

function validEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** Pure check of the attached file: null when it passes, otherwise the message (unchanged since the cream form). */
export function checkResume(file: ResumeFile | null | undefined): string | null {
  if (!file) return "Please attach your resume.";
  if (!ALLOWED_EXT.test(file.name)) return "Please upload a PDF or Word file.";
  if (file.size > MAX_BYTES) return "That file is over 5 MB. Please upload a smaller one.";
  return null;
}

/** Pure validation of the whole form. The role, the links and the note are optional. */
export function validateApply(fields: { name: string; email: string; resume: ResumeFile | null | undefined }): ApplyErrors {
  return {
    name: fields.name.trim() === "",
    email: !validEmail(fields.email.trim()),
    resume: checkResume(fields.resume),
  };
}

/** Required fields in page order: the first one in error takes the focus. */
const CONTROL_ID: Array<[keyof ApplyErrors, string]> = [
  ["name", "ap-name"],
  ["email", "ap-email"],
  ["resume", "ap-resume"],
];

const NO_ERRORS: ApplyErrors = { name: false, email: false, resume: null };

export default function ApplyForm({
  options,
  initialRole = "",
}: {
  options: string[];
  initialRole?: string;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(initialRole);
  const [links, setLinks] = useState("");
  const [note, setNote] = useState("");
  const [gotcha, setGotcha] = useState("");
  const [fileName, setFileName] = useState<string>("");

  const [errors, setErrors] = useState<ApplyErrors>(NO_ERRORS);
  // The control to focus after a failed submit; a new object each time, so the same field is focused again.
  const [focusTarget, setFocusTarget] = useState<{ id: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");

  // Read synchronously by the handler: a second click or Enter while sending is ignored, whatever React has rendered.
  const sending = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const mountedAt = useRef<number>(Date.now()); // anti-bot timing trap

  // After React has rendered the messages, so the field is announced with its description.
  useEffect(() => {
    if (focusTarget) document.getElementById(focusTarget.id)?.focus();
  }, [focusTarget]);

  useEffect(() => {
    const block = successRef.current;
    if (status !== "ok" || !block) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    block.focus({ preventScroll: true });
    block.scrollIntoView({ behavior: reduce ? "instant" : "smooth", block: "center" });
  }, [status]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending.current) return;
    const file = fileRef.current?.files?.[0] ?? null;
    const next = validateApply({ name, email, resume: file });
    setErrors(next);
    const first = CONTROL_ID.find(([key]) => next[key]);
    if (first) {
      setFocusTarget({ id: first[1] });
      return;
    }
    sending.current = true;
    setSubmitting(true);
    setStatus("idle");
    try {
      const data = new FormData();
      data.append("name", name);
      data.append("email", email);
      data.append("role", role);
      data.append("links", links);
      data.append("note", note);
      data.append("_gotcha", gotcha);
      data.append("_elapsed", String(Date.now() - mountedAt.current));
      if (file) data.append("resume", file, file.name);

      const res = await fetch("/api/apply", { method: "POST", body: data });
      setStatus(res.ok ? "ok" : "error");
    } catch {
      setStatus("error");
    } finally {
      sending.current = false;
      setSubmitting(false);
    }
  }

  const sent = status === "ok";

  return (
    // Reveal: tag, title and lead, then the reply line, then the form card, 80 ms apart (apply-form.css).
    <Reveal as="section" className="v2-apply" id="apply">
      <div className="v2-apply__inner">
        <div className="v2-apply__aside">
          <SectionHeading
            reveal
            className="v2-apply__heading"
            tag="Apply · send us your resume"
            tagVariant="blue"
            title={["Don't see your seat?", "Send it anyway."]}
            lead="Pick the role that fits, or choose general, attach your resume, and tell us why North. We read every application and reply if there's a fit."
          />
          <p className="v2-apply__built v2-reveal" style={stagger(3)}>
            We reply within <b>one week</b>
          </p>
        </div>

        <Surface className="v2-apply__card v2-reveal" style={stagger(4)}>
          {/* Form and success block share one grid cell: the card keeps its height while one fades into the other. */}
          <div className="v2-apply__body" data-state={sent ? "sent" : "open"}>
            <form
              id="applyForm"
              className="v2-apply__form"
              action="/api/apply"
              method="POST"
              encType="multipart/form-data"
              noValidate
              onSubmit={handleSubmit}
              inert={sent}
            >
              <div className="v2-apply__grid">
                <Field
                  id="ap-name"
                  name="name"
                  type="text"
                  label="Name"
                  placeholder="Your name"
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setErrors((p) => ({ ...p, name: false }));
                  }}
                  invalid={errors.name}
                  error="Please enter your name."
                />
                <Field
                  id="ap-email"
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="you@email.com"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((p) => ({ ...p, email: false }));
                  }}
                  invalid={errors.email}
                  error="Please enter a valid email."
                />
                <Field
                  control="select"
                  className="v2-apply__wide"
                  id="ap-role"
                  name="role"
                  label="Role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">Select a role</option>
                  {options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Field>
                <FileField
                  className="v2-apply__wide"
                  id="ap-resume"
                  name="resume"
                  label="Resume"
                  required
                  accept={ACCEPT}
                  inputRef={fileRef}
                  hint={fileName ? `Attached: ${fileName}` : "PDF or Word, up to 5 MB."}
                  invalid={errors.resume !== null}
                  error={errors.resume ?? "Please attach your resume."}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFileName(f?.name ?? "");
                    setErrors((p) => ({ ...p, resume: f ? checkResume(f) : null }));
                  }}
                />
                <Field
                  className="v2-apply__wide"
                  id="ap-links"
                  name="links"
                  type="text"
                  label="Links (optional)"
                  placeholder="LinkedIn, portfolio, GitHub…"
                  value={links}
                  onChange={(e) => setLinks(e.target.value)}
                />
                <Field
                  control="textarea"
                  className="v2-apply__wide"
                  id="ap-note"
                  name="note"
                  label="Why North? (optional)"
                  placeholder="A few lines on why this role, and anything we should know."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <input
                type="text"
                name="_gotcha"
                style={{ display: "none" }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
              />

              <div className="v2-apply__actions">
                <span className="v2-apply__submit">
                  <Button
                    variant="dark"
                    icon="arrow"
                    type="submit"
                    aria-disabled={submitting || undefined}
                    aria-busy={submitting || undefined}
                  >
                    Send application
                  </Button>
                  {/* Sending: a thin light runs under the label (apply-form.css), no extra word. */}
                  <span className="v2-apply__progress" aria-hidden="true" />
                </span>
                {/* Announced where it stands, so the focus can stay on the button after a failed send. */}
                <p className="v2-apply__foot" role="status" data-tone={status === "error" ? "error" : "calm"}>
                  {status === "error" ? (
                    <>
                      Something went wrong sending that. Email your resume to{" "}
                      <a href="mailto:ilan@northos.com?subject=Application">ilan@northos.com</a> and
                      we&apos;ll pick it up.
                    </>
                  ) : (
                    "Your details stay between us. Never sold, never spammed."
                  )}
                </p>
              </div>
            </form>

            <div
              ref={successRef}
              className="v2-apply__success"
              id="applySuccess"
              role="status"
              aria-live="polite"
              tabIndex={-1}
            >
              <div className="v2-apply__success-body" hidden={!sent}>
                <svg className="v2-apply__check" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                  <circle cx="20" cy="20" r="19" pathLength={1} />
                  <path d="M12.5 20.5l5 5 10-11" pathLength={1} />
                </svg>
                <p className="v2-apply__success-title">Application received.</p>
                <p className="v2-apply__success-text">
                  Thanks for sending it over. We read every one and will be in touch if there&apos;s a fit.
                </p>
              </div>
            </div>
          </div>
        </Surface>
      </div>
    </Reveal>
  );
}
